import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CianMortgageTextContainer } from './parts/CianMortgageTextContainer';
import { trackGetOnlineClick, trackDownPaymentFilterChange, trackTermFilterChange } from './tracking';
import { calculateBannerMonthlyPayment, debounceTracking, filterMortgageOffers } from './utils';
import { openPromoInfo } from '../../actions/promoInfo';
import { FlatsError } from '../../components/FlatsError';
import { MortgageOffersFilters } from '../../components/MortgageOffersFilters';
import {
  HeaderRow,
  MortgageOfferRow,
  MortgageOffersTable,
  RowCountController,
} from '../../components/MortgageOffersTable';
import { FILTERS } from '../../constants/mortgage';
import { getNewbuildingId } from '../../selectors/newbuilding';
import { selectMortgagePromos } from '../../selectors/newbuildingMorgageCalculator';
import { getOffers } from '../../selectors/offers';
import { IPromo } from '../../types/newbuilding/promos';
import { trackClickPromoBlock } from '../../utils/analytics';
import { getCianMortgagePromoUrl } from '../../utils/config';
import { calculateMonthlyPayment } from '../../utils/mortgageCalculator';
import { useApplicationContext } from '../../utils/withApplicationContext';
import { CianMortgageBannerContainer } from '../CianMortgageBanner';
import { SeoMortgageLink } from '../SeoMortgageLink';

interface IMortgageOffersContainerProps {
  onDisplayedOffersChange: (promo: IPromo[]) => void;
}

export const MortgageOffersContainer: React.FC<IMortgageOffersContainerProps> = ({ onDisplayedOffersChange }) => {
  const { fromDeveloperMinPrice, fromDeveloperMaxPrice } = useSelector(getOffers);
  const newbuildingId = useSelector(getNewbuildingId);

  const initialPrice = fromDeveloperMinPrice || FILTERS.DEFAULT_MIN_PRICE;
  const initialDownPayment = Math.round(initialPrice * FILTERS.DEFAULT_DOWN_PAYMENT_RATE);
  const maxFilterPrice = fromDeveloperMaxPrice || FILTERS.DEFAULT_MAX_PRICE;

  const [price, setPrice] = React.useState(initialPrice);
  const [downPayment, setDownPayment] = React.useState(initialDownPayment);
  const [term, setTerm] = React.useState(FILTERS.DEFAULT_LOAN_TERM);

  const allMortgagePromos = useSelector(selectMortgagePromos);
  const filteredMortgagePromos = React.useMemo(
    () => filterMortgageOffers(allMortgagePromos, price, downPayment, term),
    [allMortgagePromos, price, downPayment, term],
  );

  const bannerMonthlyPayment = calculateBannerMonthlyPayment({
    price,
    downPayment,
    loanTerm: term,
    initialPrice,
    initialDownPayment,
    filteredMortgagePromos,
  });

  const dispatch = useDispatch();

  const { config } = useApplicationContext();
  const mortgagePartnerLink = getCianMortgagePromoUrl(config);

  const trackDownPaymentFilterChangeDebounced = React.useCallback(debounceTracking(trackDownPaymentFilterChange), []);
  const trackTermFilterChangeDebounced = React.useCallback(debounceTracking(trackTermFilterChange), []);

  const handleRowClick = React.useCallback(
    (promo: IPromo) => {
      dispatch(openPromoInfo({ promo }));
      trackClickPromoBlock(promo, newbuildingId);
    },
    [dispatch, newbuildingId],
  );

  const handleGetOnlineClick = React.useCallback(
    (promo: IPromo) => {
      const index = filteredMortgagePromos.findIndex(
        item => item.uniquePromoId === promo.uniquePromoId && item.promoType === promo.promoType,
      );
      trackGetOnlineClick(promo, index + 1, newbuildingId);
    },
    [filteredMortgagePromos, newbuildingId],
  );

  const handleDownPaymentChange = React.useCallback(
    (value: number) => {
      setDownPayment(value);
      trackDownPaymentFilterChangeDebounced();
    },
    [trackDownPaymentFilterChangeDebounced],
  );

  const handleTermChange = React.useCallback(
    (value: number) => {
      setTerm(value);
      trackTermFilterChangeDebounced();
    },
    [trackTermFilterChangeDebounced],
  );

  return (
    <div data-testid="MortgageOffers">
      <MortgageOffersFilters
        price={price}
        downPayment={downPayment}
        term={term}
        maxPrice={maxFilterPrice}
        onPriceChanged={setPrice}
        onDownPaymentChanged={handleDownPaymentChange}
        onTermChanged={handleTermChange}
        onDownPaymentMouseDown={trackDownPaymentFilterChange}
        onTermMouseDown={trackTermFilterChange}
      />
      <MortgageOffersTable>
        <RowCountController
          promos={filteredMortgagePromos}
          mortgageSeoLink={<SeoMortgageLink />}
          onDisplayedOffersChange={onDisplayedOffersChange}
          content={promos => (
            <>
              <HeaderRow />
              {promos.map(promo => (
                <MortgageOfferRow
                  key={promo.uniquePromoId}
                  promoInfo={promo}
                  onClick={handleRowClick}
                  onGetOnlineClick={handleGetOnlineClick}
                  monthlyPayment={calculateMonthlyPayment({
                    initialPayment: downPayment,
                    interestRate: promo.mortgageParams?.interestRate,
                    loanTerm: term,
                    price,
                  })}
                  mortgagePartnerLink={mortgagePartnerLink}
                />
              ))}
            </>
          )}
          error={
            <FlatsError
              image="not-found"
              title="Нет подходящих предложений от банков и застройщиков"
              text={<CianMortgageTextContainer />}
            />
          }
        />
      </MortgageOffersTable>
      <CianMortgageBannerContainer monthlyPayment={bannerMonthlyPayment} />
    </div>
  );
};
